import type {PaidPlanId} from "pricing/planCatalogue";
import {isOverHostedUploadLimit} from "pricing/planEntitlements";
import type {
	OverLimitOffer, OverLimitPlanOffer, OverLimitPricing, OverLimitResolution, PassWall,
} from "./overLimitOffer";
import {choosePayRow, offeredPlanId, type PayDialogState} from "./overLimitPayDialog";

/**
 * The rules of the sized plan dialog (kitecorp/transferify-ui#3800): what the size wall of either delivery path
 * offers once the one-off is gone. Plain TypeScript: the view model reads it, no React here.
 *
 * Why the ui still filters the plans by cap on the hosted path: the answer is meant to list only the plans whose hosted
 * cap holds the drop (an api from before W2, kitecorp/transferify-api-service#3017, sends one plan), and the api refuses
 * a hosted upload over the sender's plan cap at registration ({@link isOverHostedUploadLimit} is that boundary), so a
 * hosted plan that cannot hold the drop is never shown, whatever an answer carries. An Instant send has no such cap,
 * so every paid plan of the answer is offered at any size.
 */

/** The plan rows the dialog shows, the one in view, and the sender's pick. */
export interface SizedPlanRows {
	plans: readonly OverLimitPlanOffer[];
	chosen: PaidPlanId;
	choose: (plan: PaidPlanId) => void;
}

/** A plan with no cap holds any drop, so it sorts after every capped one. */
function capOrder(plan: OverLimitPlanOffer): number {
	return plan.maxHostedUploadBytes ?? Number.POSITIVE_INFINITY;
}

/** Kept at module level so "no rows" has one identity across renders. */
const NO_SIZED_PLANS: readonly OverLimitPlanOffer[] = [];

/**
 * The paid plans the dialog offers for this drop, smallest cap first, one row per plan. The source is the answer's list
 * of plans, or its single plan for an api from before W2 (kitecorp/transferify-api-service#3017). On the hosted path
 * only the plans whose cap holds the drop; on the Instant path every paid plan, because an Instant send has no cap
 * (Q5). Nothing is offered while no quote is in.
 */
export function fittingPlanOffers(
	offer: OverLimitOffer | null, selectedBytes: number, pricing: OverLimitPricing,
): readonly OverLimitPlanOffer[] {
	const sourced = offer?.planOffers ?? (offer?.planOffer ? [offer.planOffer] : []);
	return sourced
		.filter(plan => offeredPlanId(plan) !== null
			&& (pricing === "instant" || !isOverHostedUploadLimit(plan.maxHostedUploadBytes ?? null, selectedBytes)))
		.filter((plan, index, held) => held.findIndex(other => other.planCode === plan.planCode) === index)
		// Stable since ES2019, so plans with the same cap keep the api's order.
		.sort((a, b) => capOrder(a) - capOrder(b));
}

/** The rows of the size wall of either path; the storage-full strip keeps its own rungs and gets none. */
export function sizedPlanOffers(
	wall: PassWall | null, pricing: OverLimitPricing, offer: OverLimitOffer | null, selectedBytes: number,
): readonly OverLimitPlanOffer[] {
	return wall === "size" ? fittingPlanOffers(offer, selectedBytes, pricing) : NO_SIZED_PLANS;
}

/**
 * The wall as the dialog reads it (Q3, "plans only", carried out by Ruling 1). On the size wall the plan rung takes the
 * first row of the list that fits the drop, or none where no row is wired, in place of the quote's own single row. A held
 * purchase and every other rung (a clear send, a sign-in, a refusal, the storage wall's plan rung, which offers no row)
 * stay as they were.
 */
export function plansOnlyResolution(
	resolution: OverLimitResolution, wall: PassWall | null, plans: readonly OverLimitPlanOffer[],
): OverLimitResolution {
	if (wall === "size" && resolution.kind === "planRequired") {
		return {kind: "planRequired", planOffer: plans[0] ?? null};
	}
	return resolution;
}

/** The sender's row while it is still offered, else the smallest one offered, else none. */
export function chosenSizedPlan(
	choice: PaidPlanId | null, plans: readonly OverLimitPlanOffer[],
): {plan: PaidPlanId; offer: OverLimitPlanOffer} | null {
	const row = plans.find(plan => offeredPlanId(plan) === choice) ?? plans[0];
	const plan = offeredPlanId(row);
	return row === undefined || plan === null ? null : {plan, offer: row};
}

/** The choosing step with the plan row chosen, so Continue opens the order form at once; the payment step is kept. */
export function preselectedPlanRow(state: PayDialogState): PayDialogState {
	return choosePayRow(state, "plan");
}

/**
 * A plan order the api refused as a claim it is not offering (a stale quote, a forged request;
 * kitecorp/transferify-api-service#3017) closes the order form, whichever wall the dialog is on: the owner decided on
 * 2026-10-27 that both walls have one flow, with no error state on the order form. Only an open dialog closes, so the
 * closing does not repeat once it has.
 */
export function closesOnRefusedClaim(open: boolean, offerUnavailable: boolean): boolean {
	return open && offerUnavailable;
}

/**
 * What the form does with the quote it asked again after a refused claim. Nothing while no refusal is pending, while the
 * dialog is open, while the fresh quote has not answered, or while the rung is still waiting for an answer. Then the
 * same sized dialog reopens on the fresh rows when the api still offers a plan, and the reopening is dropped otherwise
 * (the strip states what the api offers now). `quoteRenewed` is the purchase hook's answer count having moved past the
 * value saved at the refusal (`quoteAnswers`): a settled answer of any kind, an offer, no offer or a failure.
 */
export function reopenAfterRefusedClaim(
	pending: boolean, quoteRenewed: boolean, resolution: OverLimitResolution, open: boolean,
): "reopen" | "drop" | null {
	if (!pending || open || !quoteRenewed || resolution.kind === "quoting" || resolution.kind === "checkingPurchase") {
		return null;
	}
	return resolution.kind === "planRequired" && resolution.planOffer !== null ? "reopen" : "drop";
}
